// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { getUserFacingErrorMessage, handleAPIError } from '@/api/error-utils';
import {
  MarkInvoiceAsSentInput,
  markInvoiceAsSent,
} from '@/billing/api/mark-invoice-as-sent';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

// User-facing validation errors — routed to the known-error snackbar rather
// than Sentry. Unexpected failures (network, 500) still fire Sentry.
const EXPECTED_ERROR_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_not_sendable,
  ERROR_CODE_MAPPING.invoice_sent_at_future,
]);

interface UseMarkInvoiceAsSentOptions {
  invoiceKey: string;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function useMarkInvoiceAsSent(options: UseMarkInvoiceAsSentOptions) {
  const queryClient = useQueryClient();

  const invalidateAll = () =>
    invalidateInvoiceQueries({
      queryClient,
      invoiceKey: options.invoiceKey,
    });

  return useMutation({
    mutationFn: (input: MarkInvoiceAsSentInput) => markInvoiceAsSent(input),
    onSuccess: async () => {
      await invalidateAll();
      options.onSuccess?.();
    },
    onError: async (error: AxiosError) => {
      const apiError = handleAPIError(error);
      await invalidateAll();
      if (
        !apiError.sentToSentry &&
        !EXPECTED_ERROR_CODES.has(apiError.apiStatusCode)
      ) {
        Sentry.captureException(error);
      }
      options.onError?.(
        getUserFacingErrorMessage(apiError),
        apiError.apiStatusCode,
        apiError.httpStatusCode
      );
    },
  });
}
